// TableComandas.jsx

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Table,
} from "react-bootstrap";

import {
  getComandas,
  deleteComanda,
  getComandaById,
} from "../helpers/rutaComandas";

import ModalComanda from "./ModalComanda";
import DocumentoPrint from "./DocumentoPrint";
import RemitoComanda from "../plantillas/RemitoComanda";

import "../css/tablecomandas.css";

const REGISTROS_POR_PAGINA = 12;
const PAGINAS_VISIBLES = 4;

export default function TableComandas() {
  const [
    lista,
    setLista,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    comanda,
    setComanda,
  ] = useState({});

  const [
    show,
    setShow,
  ] = useState(false);

  const [
    busquedaViaje,
    setBusquedaViaje,
  ] = useState("");

  const [
    pagina,
    setPagina,
  ] = useState(1);

  /* ======================================================
     CARGA
  ====================================================== */

  useEffect(() => {
    fetchComandas();
  }, []);

  const fetchComandas = () => {
    setLoading(true);

    getComandas().then((d) => {
      const arr =
        Array.isArray(
          d?.comandas
        )
          ? d.comandas
          : Array.isArray(d)
          ? d
          : [];

      const ordenadas =
        [...arr].sort(
          (a, b) => {
            const nroA =
              Number(
                a.codcom ||
                  0
              );

            const nroB =
              Number(
                b.codcom ||
                  0
              );

            return (
              nroB -
              nroA
            );
          }
        );

      setLista(
        ordenadas
      );

      setLoading(
        false
      );
    });
  };

  /* ======================================================
     MODAL
  ====================================================== */

  const handleClose = () => {
    setShow(false);

    fetchComandas();
  };

  const modificaComanda = (
    id
  ) => {
    getComandaById(
      id
    ).then((r) => {
      if (r.ok) {
        setComanda(
          r.comanda
        );

        setShow(true);
      }
    });
  };

  const eliminarComanda = (
    id
  ) => {
    if (
      window.confirm(
        "¿Borrar comanda?"
      )
    ) {
      deleteComanda(
        id
      ).then(
        fetchComandas
      );
    }
  };

  /* ======================================================
     FILTRO
  ====================================================== */

  const listaFiltrada =
    useMemo(() => {
      const texto =
        busquedaViaje
          .trim()
          .toLowerCase();

      return lista.filter(
        (c) => {
          if (!c.activo) {
            return false;
          }

          if (!texto) {
            return true;
          }

          return String(
            c.nroviaje ||
              ""
          )
            .toLowerCase()
            .includes(
              texto
            );
        }
      );
    }, [
      lista,
      busquedaViaje,
    ]);

  /* ======================================================
     PAGINACIÓN
  ====================================================== */

  useEffect(() => {
    setPagina(1);
  }, [busquedaViaje]);

  const totalPaginas =
    Math.max(
      1,
      Math.ceil(
        listaFiltrada.length /
          REGISTROS_POR_PAGINA
      )
    );

  useEffect(() => {
    if (
      pagina >
      totalPaginas
    ) {
      setPagina(
        totalPaginas
      );
    }
  }, [
    pagina,
    totalPaginas,
  ]);

  const inicio =
    (
      pagina -
      1
    ) *
    REGISTROS_POR_PAGINA;

  const fin =
    inicio +
    REGISTROS_POR_PAGINA;

  const listaPagina =
    listaFiltrada.slice(
      inicio,
      fin
    );

  const paginas =
    useMemo(() => {
      let primera =
        pagina -
        1;

      if (
        primera <
        1
      ) {
        primera = 1;
      }

      let ultima =
        primera +
        PAGINAS_VISIBLES -
        1;

      if (
        ultima >
        totalPaginas
      ) {
        ultima =
          totalPaginas;

        primera =
          Math.max(
            1,
            ultima -
              PAGINAS_VISIBLES +
              1
          );
      }

      const resultado =
        [];

      for (
        let i = primera;
        i <= ultima;
        i++
      ) {
        resultado.push(i);
      }

      return resultado;
    }, [
      pagina,
      totalPaginas,
    ]);

  /* ======================================================
     LOADING
  ====================================================== */

  if (loading) {
    return (
      <div className="tc2-loading">
        Cargando viajes...
      </div>
    );
  }

  /* ======================================================
     RENDER
  ====================================================== */

  return (
    <div className="tc2-card">

      {/* CABECERA */}

      <div className="tc2-toolbar">
        <div className="tc2-toolbar-title">
          <h4>
            Viajes registrados
          </h4>

          <span>
            {
              listaFiltrada.length
            }{" "}
            registros activos
          </span>
        </div>

        <div className="tc2-search">
          <label>
            Buscar N° Viaje
          </label>

          <input
            type="text"
            className="form-control"
            placeholder="Ej: 1595"
            value={
              busquedaViaje
            }
            onChange={(
              e
            ) =>
              setBusquedaViaje(
                e.target
                  .value
              )
            }
          />

          {busquedaViaje && (
            <button
              type="button"
              className="tc2-clear"
              onClick={() =>
                setBusquedaViaje(
                  ""
                )
              }
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* INFO */}

      <div className="tc2-info">
        <span>
          Mostrando{" "}
          <strong>
            {listaFiltrada.length
              ? inicio + 1
              : 0}
          </strong>{" "}
          a{" "}
          <strong>
            {Math.min(
              fin,
              listaFiltrada.length
            )}
          </strong>{" "}
          de{" "}
          <strong>
            {
              listaFiltrada.length
            }
          </strong>
        </span>

        <span>
          Página{" "}
          <strong>
            {pagina}
          </strong>{" "}
          de{" "}
          <strong>
            {
              totalPaginas
            }
          </strong>
        </span>
      </div>

      {/* TABLA */}

      <div className="tc2-table-scroll">
        <Table
          striped
          bordered
          hover
          className="table-container tc2-table"
        >
          <thead>
            <tr>
              <th
                className="acciones"
                style={{
                  width: 120,
                  minWidth: 120,
                  maxWidth: 120,
                  whiteSpace:
                    "nowrap",
                }}
              >
                Acciones
              </th>

              <th className="sticky codcom">
                Cód.
              </th>

              <th className="sticky viaje">
                Nro Viaje
              </th>

              <th
                style={{
                  width: 120,
                  minWidth: 120,
                  maxWidth: 120,
                  whiteSpace:
                    "nowrap",
                }}
              >
                Empresa
              </th>

              <th
                style={{
                  width: 120,
                  minWidth: 120,
                  maxWidth: 120,
                  whiteSpace:
                    "nowrap",
                }}
              >
                Transporte
              </th>

              <th
                style={{
                  width: 120,
                  minWidth: 120,
                  maxWidth: 120,
                  whiteSpace:
                    "nowrap",
                }}
              >
                Chofer
              </th>

              <th>
                Cliente Carga
              </th>

              <th>
                Fecha Viaje
              </th>

              <th
                style={{
                  width: 140,
                  minWidth: 140,
                  maxWidth: 140,
                  whiteSpace:
                    "nowrap",
                }}
              >
                Nro. Factura
              </th>

              <th>
                Estado
              </th>

              <th>
                Tercero
              </th>

              <th>
                Cliente Carga
              </th>

              <th>
                Origen
              </th>

              <th>
                Destino
              </th>

              <th>
                Cliente Descarga
              </th>

              <th>
                Remitos
              </th>

              <th>
                Producto
              </th>

              <th>
                Camión
              </th>

              <th>
                Estado
              </th>

              <th>
                Kg. Salida
              </th>

              <th>
                Kg. Llegada
              </th>

              <th>
                Diferencia
              </th>

              <th>
                Pactado
              </th>

              <th>
                Subtotal
              </th>

              <th>
                IVA
              </th>

              <th>
                Total
              </th>

              <th style={{ color: "#dc3545" }}>Pactado Tercero</th>
              <th style={{ color: "#dc3545" }}>Comisión</th>
              <th style={{ color: "#dc3545" }}>Merma</th>

              <th>
                Km. Salida
              </th>

              <th>
                Km Destino
              </th>

              <th
                style={{
                  width: 200,
                  minWidth: 200,
                  maxWidth: 200,
                  whiteSpace:
                    "nowrap",
                }}
              >
                Observaciones
              </th>
            </tr>
          </thead>

          <tbody>
            {listaPagina.length ===
            0 ? (
              <tr>
                <td
                  colSpan="32"
                  className="tc2-empty"
                >
                  No se encontraron
                  viajes.
                </td>
              </tr>
            ) : (
              listaPagina.map(
                (c) => (
                  <tr
                    key={
                      c._id
                    }
                  >
                    <td className="sticky actions acciones">
                      <button
                        className="btn btn-primary btn-icon btn-xs"
                        title="Editar"
                        onClick={() =>
                          modificaComanda(
                            c._id
                          )
                        }
                      >
                        <i className="fa fa-pencil" />
                      </button>

                      <DocumentoPrint
                        Plantilla={
                          RemitoComanda
                        }
                        data={
                          c
                        }
                        trigger={({
                          onClick,
                        }) => (
                          <button
                            className="btn btn-secondary btn-icon btn-xs ml-1"
                            title="Imprimir / PDF"
                            onClick={
                              onClick
                            }
                          >
                            <i className="fa fa-print" />
                          </button>
                        )}
                      />
                    </td>

                    <td className="sticky codcom">
                      {
                        c.codcom
                      }
                    </td>

                    <td className="sticky viaje">
                      {
                        c.nroviaje
                      }
                    </td>

                    <td className="dom">
                      {c.codemp
                        ?.empresa ||
                        "-"}
                    </td>

                    <td className="dom">
                      {c.codtra
                        ?.transporte ||
                        "-"}
                    </td>

                    <td className="dom">
                      {c.codcho
                        ?.nombre ||
                        "-"}
                    </td>

                    <td className="dom">
                      {c.codclif
                        ?.razonsocial ||
                        "-"}
                    </td>

                    <td>
                      {c.fecha
                        ? new Date(
                            c.fecha
                          ).toLocaleDateString(
                            "es-AR",
                            {
                              day:
                                "2-digit",
                              month:
                                "2-digit",
                              year:
                                "numeric",
                            }
                          )
                        : "-"}
                    </td>

                    <td>
                      {
                        c.factura ||
                        "-"
                      }
                    </td>

                    <td className="dom">
                      {
                        c.estado ||
                        "-"
                      }
                    </td>

                    <td className="dom">
                      {
                        c.tercero ||
                        "-"
                      }
                    </td>

                    <td className="dom">
                      {c.codclic
                        ?.razonsocial ||
                        "-"}
                    </td>

                    <td className="dom">
                      {c.codori
                        ?.destino ||
                        "-"}
                    </td>

                    <td className="dom">
                      {c.coddes
                        ?.destino ||
                        "-"}
                    </td>

                    <td className="dom">
                      {c.codclid
                        ?.razonsocial ||
                        "-"}
                    </td>

                    <td className="dom">
                      {
                        c.remitos ||
                        "-"
                      }
                    </td>

                    <td className="dom">
                      {c.codpro
                        ?.producto ||
                        "-"}
                    </td>

                    <td className="dom">
                      {c.codcam
                        ?.camion ||
                        "-"}
                    </td>

                    <td className="dom">
                      {c.codest
                        ?.estado ||
                        "-"
                      }
                    </td>

                    <td style={{
                      textAlign:
                        "right",
                    }}>
                      {c.kgsal?.toLocaleString(
                        "es-AR",
                        {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }
                      ) ||
                        "-"}
                    </td>

                    <td style={{
                      textAlign:
                        "right",
                    }}>
                      {c.kglle?.toLocaleString(
                        "es-AR",
                        {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }
                      ) ||
                        "-"}
                    </td>

                    <td style={{
                      textAlign:
                        "right",
                    }}>
                      {c.dif?.toLocaleString(
                        "es-AR",
                        {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }
                      ) ||
                        "-"}
                    </td>

                    <td
                      className="dom"
                      style={{
                        textAlign:
                          "right",
                      }}
                    >
                      {c.pactado?.toLocaleString(
                        "es-AR",
                        {
                          style:
                            "currency",
                          currency:
                            "ARS",
                          minimumFractionDigits: 2,
                        }
                      ) ||
                        "-"}
                    </td>

                    <td
                      className="dom"
                      style={{
                        textAlign:
                          "right",
                      }}
                    >
                      {c.subtotal?.toLocaleString(
                        "es-AR",
                        {
                          style:
                            "currency",
                          currency:
                            "ARS",
                          minimumFractionDigits: 2,
                        }
                      ) ||
                        "-"}
                    </td>

                    <td
                      className="dom"
                      style={{
                        textAlign:
                          "right",
                      }}
                    >
                      {c.iva?.toLocaleString(
                        "es-AR",
                        {
                          style:
                            "currency",
                          currency:
                            "ARS",
                          minimumFractionDigits: 2,
                        }
                      ) ||
                        "-"}
                    </td>

                    <td
                      className="dom"
                      style={{
                        textAlign:
                          "right",
                      }}
                    >
                      {c.total?.toLocaleString(
                        "es-AR",
                        {
                          style:
                            "currency",
                          currency:
                            "ARS",
                          minimumFractionDigits: 2,
                        }
                      ) ||
                        "-"}
                    </td>

                    <td
                      className="dom"
                      style={{ textAlign: "right", color: "#dc3545", fontWeight: 600 }}
                    >
                      {c.pactadoter?.toLocaleString("es-AR", {
                        style: "currency",
                        currency: "ARS",
                        minimumFractionDigits: 2,
                      }) ?? "-"}
                    </td>

                    <td
                      className="dom"
                      style={{ textAlign: "right", color: "#dc3545", fontWeight: 600 }}
                    >
                      {Number(c.comision ?? 0).toLocaleString("es-AR", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>

                    <td
                      className="dom"
                      style={{ textAlign: "right", color: "#dc3545", fontWeight: 600 }}
                    >
                      {Number(c.merma ?? 0).toLocaleString("es-AR", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>

                    <td
                      className="dom"
                      style={{
                        textAlign:
                          "right",
                      }}
                    >
                      {c.kmsal?.toLocaleString(
                        "es-AR",
                        {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }
                      ) ||
                        "-"}
                    </td>

                    <td
                      className="dom"
                      style={{
                        textAlign:
                          "right",
                      }}
                    >
                      {c.kmdes?.toLocaleString(
                        "es-AR",
                        {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }
                      ) ||
                        "-"}
                    </td>

                    <td className="textobs">
                      {
                        c.observ ||
                        "-"
                      }
                    </td>
                  </tr>
                )
              )
            )}
          </tbody>
        </Table>
      </div>

      {/* PAGINACIÓN */}

      <div className="tc2-pagination">
        <span className="tc2-page-size">
          12 viajes por página
        </span>

        <div className="tc2-pagination-buttons">

          <button
            type="button"
            disabled={
              pagina === 1
            }
            onClick={() =>
              setPagina(1)
            }
          >
            «
          </button>

          <button
            type="button"
            disabled={
              pagina === 1
            }
            onClick={() =>
              setPagina(
                pagina - 1
              )
            }
          >
            ‹
          </button>

          {paginas.map(
            (numero) => (
              <button
                type="button"
                key={
                  numero
                }
                className={
                  numero ===
                  pagina
                    ? "active"
                    : ""
                }
                onClick={() =>
                  setPagina(
                    numero
                  )
                }
              >
                {numero}
              </button>
            )
          )}

          <button
            type="button"
            disabled={
              pagina ===
              totalPaginas
            }
            onClick={() =>
              setPagina(
                pagina + 1
              )
            }
          >
            ›
          </button>

          <button
            type="button"
            disabled={
              pagina ===
              totalPaginas
            }
            onClick={() =>
              setPagina(
                totalPaginas
              )
            }
          >
            »
          </button>

        </div>
      </div>

      <ModalComanda
        show={
          show
        }
        handleClose={
          handleClose
        }
        comanda={
          comanda
        }
      />
    </div>
  );
}